'use client';

import { type FlexProps } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { type LinkProps } from '@ironhack/design-system2/components/link';
import { type TextProps } from '@ironhack/design-system2/components/text';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { StorytellingBase } from '@/app/components/client/storytelling-base';
import { CmsImage } from '@/app/components/server/cms-image';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms';

export type StorytellingCard = {
  title?: string;
  content?: StructuredTextGraphQlResponse;
};
export type Storytelling = DatoBlock<{
  columns: Array<{
    title?: string;
    subtitle?: string;
    image?: DatoImage;
    card?: [StorytellingCard];
    button?: [DatoButtonBlock & Pick<LinkProps, 'rel'>];
    flexProps?: FlexProps;
  }>;
  headingProps?: TextProps;
  moduleFormat: [DatoModuleFormat];
  tag?: string;
  title?: string;
}>;

type StorytellingProps = { data: Storytelling } & FlexProps;

export const Storytelling = forwardRef(
  (props: StorytellingProps, ref): ReactElement => {
    const { data, as = 'section', title: navTitle, ...flexProps } = props;
    const {
      columns,
      moduleFormat: [moduleFormat],
      headingProps = {},
      tag,
      title,
    } = data;

    return (
      <ModuleWrapper
        innerProps={flexProps}
        moduleFormat={moduleFormat}
        outerProps={{
          as,
          ...flexProps,
        }}
        ref={ref}
        title={navTitle}
      >
        <StorytellingBase>
          {tag && <StorytellingBase.Tag text={tag} />}
          {title && (
            <StorytellingBase.Title {...headingProps}>
              {title}
            </StorytellingBase.Title>
          )}
          {columns.map((column, index) => (
            <StorytellingBase.Column
              key={column.title ?? index}
              {...column.flexProps}
            >
              {column.title && (
                <StorytellingBase.Title {...headingProps}>
                  {column.title}
                </StorytellingBase.Title>
              )}
              {column.subtitle && (
                <StorytellingBase.Subtitle>
                  {column.subtitle}
                </StorytellingBase.Subtitle>
              )}
              {column.image && (
                <StorytellingBase.Media>
                  <CmsImage
                    data={column.image.responsiveImage}
                    {...(as === 'header' && { priority: true })}
                    sizes={`(min-width: 768px) ${
                      columns.length > 1 ? '548px' : '1120px'
                    }, 100vw`}
                  />
                </StorytellingBase.Media>
              )}
              {column.card?.[0] && (
                <StorytellingBase.Card
                  content={column.card[0].content}
                  title={column.card[0].title}
                />
              )}
              {column.button?.[0] && (
                <StorytellingBase.Action button={column.button[0]} />
              )}
            </StorytellingBase.Column>
          ))}
        </StorytellingBase>
      </ModuleWrapper>
    );
  }
);

export const storytellingFragment = `
  fragment storytellingFragment on StorytellingRecord {
    __typename
    moduleFormat {
      ...moduleFormatFragment
    }
    columns {
      title
      subtitle
      image {
        responsiveImage {
          ...responsiveImageFragment
        }
      }
      card {
        title
        content {
          blocks {
            ...listWithIconsFragment
          }
          links
          value
        }
      }
      button {
        ...buttonFragment
      }
    }
    tag
    title
  }
`;
